import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Search, Download, Eye, Shield, Filter,
  Loader2, ExternalLink, ChevronDown
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VISIBILITY_OPTIONS = [
  { value: "internal_only", label: "Internal Only", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "borrower_visible", label: "Borrower", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "lender_visible", label: "Lender", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "investor_visible", label: "Investor", color: "bg-purple-100 text-purple-700 border-purple-200" },
  { value: "committed_only", label: "Committed Only", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "all_participants", label: "All Participants", color: "bg-teal-100 text-teal-700 border-teal-200" },
];

const DOC_TYPE_LABELS = {
  tax_returns: "Tax Returns", bank_statements: "Bank Statements",
  entity_docs: "Entity Docs", insurance: "Insurance", appraisal: "Appraisal",
  title: "Title", borrower_summary: "Borrower Summary", deal_summary: "Deal Summary",
  underwriting_report: "UW Report", deal_packet: "Deal Packet", report: "Report",
};

export default function DocumentGovernancePanel({ onViewDeal }) {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [visFilter, setVisFilter] = useState("all");

  const headers = { Authorization: `Bearer ${localStorage.getItem("ic_token")}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/documents/governance`, { headers });
      setDocs(res.data.recent_documents || []);
      setStats(res.data.visibility_breakdown || {});
      setTotal(res.data.total_documents || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load documents");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleVisibilityChange = async (docId, newVis) => {
    try {
      await axios.patch(`${API}/admin/documents/${docId}/visibility`, { visibility: newVis }, { headers });
      setDocs(prev => prev.map(d => d.doc_id === docId ? { ...d, visibility: newVis } : d));
      toast.success("Visibility updated");
    } catch (err) {
      toast.error("Failed to update visibility");
    }
  };

  const handleDownload = async (doc) => {
    try {
      if (doc.url) {
        window.open(doc.url, "_blank");
      } else if (doc.storage_path) {
        const res = await axios.get(`${API}/documents/${doc.doc_id}/download-url`, { headers });
        if (res.data.url) window.open(res.data.url, "_blank");
        else toast.error("No download URL available");
      }
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const filtered = docs.filter(d => {
    if (visFilter !== "all" && d.visibility !== visFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (d.filename || "").toLowerCase().includes(q) ||
             (d.deal_number || "").toLowerCase().includes(q) ||
             (d.borrower_name || "").toLowerCase().includes(q) ||
             (d.doc_type || "").toLowerCase().includes(q);
    }
    return true;
  });

  const getVisBadge = (vis) => {
    const opt = VISIBILITY_OPTIONS.find(v => v.value === vis);
    return opt ? opt : { label: vis?.replace(/_/g, " "), color: "bg-gray-100 text-gray-700 border-gray-200" };
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-[#888]">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading documents...
    </div>
  );

  return (
    <div className="space-y-5" data-testid="document-governance-panel">
      {/* Header Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#222]">Document Governance</h2>
          <p className="text-xs text-[#888] mt-0.5">{total} document{total !== 1 ? "s" : ""} across all deals</p>
        </div>
        <div className="flex items-center gap-2">
          {Object.entries(stats).filter(([, v]) => v > 0).map(([key, count]) => {
            const badge = getVisBadge(key);
            return (
              <button key={key} onClick={() => setVisFilter(visFilter === key ? "all" : key)}
                className={`px-2 py-1 rounded-lg text-[9px] font-bold border transition-all ${
                  visFilter === key ? badge.color + " ring-1 ring-offset-1 ring-current" : "bg-[#faf7f2] text-[#888] border-[#e8e0d0]"
                }`} data-testid={`doc-filter-${key}`}>
                {count} {badge.label}
              </button>
            );
          })}
          {visFilter !== "all" && (
            <button onClick={() => setVisFilter("all")} className="text-[10px] text-red-500 underline">Clear</button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#aaa]" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by filename, deal number, borrower, or type..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#e8e0d0] bg-white text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none"
          data-testid="doc-search-input" />
      </div>

      {/* Document Table */}
      <div className="rounded-xl border border-[#e8e0d0] overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#faf7f2] border-b border-[#e8e0d0]">
              <th className="text-left px-4 py-3 text-[10px] text-[#888] uppercase tracking-wider font-semibold">Document</th>
              <th className="text-left px-4 py-3 text-[10px] text-[#888] uppercase tracking-wider font-semibold">Deal</th>
              <th className="text-left px-4 py-3 text-[10px] text-[#888] uppercase tracking-wider font-semibold">Type</th>
              <th className="text-left px-4 py-3 text-[10px] text-[#888] uppercase tracking-wider font-semibold">Visibility</th>
              <th className="text-left px-4 py-3 text-[10px] text-[#888] uppercase tracking-wider font-semibold">Date</th>
              <th className="text-right px-4 py-3 text-[10px] text-[#888] uppercase tracking-wider font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#f0ece2]">
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#888]">No documents match your filters</td></tr>
            ) : filtered.map(doc => {
              const visBadge = getVisBadge(doc.visibility);
              return (
                <tr key={doc.doc_id} className="hover:bg-[#fdfcf9] transition-colors" data-testid={`doc-row-${doc.doc_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#D4AF37] shrink-0" />
                      <div>
                        <div className="font-medium text-[#222] truncate max-w-[200px]">{doc.filename || doc.original_filename || "Untitled"}</div>
                        {doc.content_type && <div className="text-[9px] text-[#aaa] mt-0.5">{doc.content_type}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {doc.deal_number ? (
                      <button onClick={() => onViewDeal?.(doc.lead_id)} className="text-[#D4AF37] hover:underline font-medium" data-testid={`doc-deal-link-${doc.doc_id}`}>
                        {doc.deal_number}
                      </button>
                    ) : <span className="text-[#ccc]">—</span>}
                    {doc.borrower_name && <div className="text-[9px] text-[#aaa] mt-0.5">{doc.borrower_name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-[#faf7f2] text-[#666] border border-[#e8e0d0]">
                      {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative inline-block">
                      <select value={doc.visibility || "internal_only"}
                        onChange={e => handleVisibilityChange(doc.doc_id, e.target.value)}
                        className={`appearance-none pl-2 pr-6 py-1 rounded-lg text-[9px] font-bold border cursor-pointer ${visBadge.color}`}
                        data-testid={`doc-visibility-${doc.doc_id}`}>
                        {VISIBILITY_OPTIONS.map(v => (
                          <option key={v.value} value={v.value}>{v.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-2.5 w-2.5 pointer-events-none" />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#888]">
                    {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => handleDownload(doc)} title="Download"
                        className="p-1.5 rounded-lg hover:bg-[#f0ece2] text-[#888] hover:text-[#333] transition-colors"
                        data-testid={`doc-download-${doc.doc_id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
